import { Link } from "react-router-dom";
import { SUPPORT_NUMBERS } from "@/lib/constants";
import { useSiteContent } from "@/lib/siteContent";
import { Facebook, Instagram, Twitter, Linkedin, Youtube, Music2 } from "lucide-react";

const SOCIAL_LINKS = [
  { key: "social_facebook", icon: Facebook, label: "Facebook" },
  { key: "social_instagram", icon: Instagram, label: "Instagram" },
  { key: "social_twitter", icon: Twitter, label: "X (Twitter)" },
  { key: "social_tiktok", icon: Music2, label: "TikTok" },
  { key: "social_linkedin", icon: Linkedin, label: "LinkedIn" },
  { key: "social_youtube", icon: Youtube, label: "YouTube" },
];

export default function Footer() {
  const sc = useSiteContent();
  const socials = SOCIAL_LINKS.filter((s) => (sc[s.key] || "").trim());
  return (
    <footer className="border-t border-gray-200 mt-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-2">
            <img
              src="/brand/swiftlink-logo.png"
              alt="Swiftlink-Africa"
              className="h-10 w-10 object-contain"
            />
            <div className="font-heading text-lg font-bold">Swiftlink-Africa</div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {sc.footer_tagline || "Transfert d'argent rapide et sécurisé entre le Togo, le Gabon, la France et le Rwanda."}
          </p>
          {socials.length > 0 && (
            <div className="mt-4 flex items-center gap-2" data-testid="footer-socials">
              {socials.map(({ key, icon: Icon, label }) => (
                <a
                  key={key}
                  href={sc[key]}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  title={label}
                  className="w-9 h-9 rounded-full grid place-items-center border border-gray-200 text-gray-500 hover:border-[var(--sl-primary)] hover:text-[var(--sl-primary)] hover:-translate-y-0.5 transition"
                  data-testid={`footer-social-${key.replace("social_", "")}`}
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          )}
        </div>
        <div>
          <h4 className="font-heading font-semibold mb-3">Produit</h4>
          <ul className="text-sm space-y-2 text-gray-600">
            <li><Link to="/envoyer" className="hover:text-[var(--sl-primary)]">Envoyer de l&apos;argent</Link></li>
            <li><Link to="/suivi" className="hover:text-[var(--sl-primary)]">Suivre un envoi</Link></li>
            <li><Link to="/faq" className="hover:text-[var(--sl-primary)]">FAQ</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-heading font-semibold mb-3">Compte</h4>
          <ul className="text-sm space-y-2 text-gray-600">
            <li><Link to="/login" className="hover:text-[var(--sl-primary)]">Connexion</Link></li>
            <li><Link to="/register" className="hover:text-[var(--sl-primary)]">Inscription</Link></li>
            <li><Link to="/parrainage" className="hover:text-[var(--sl-primary)]">Parrainage</Link></li>
            <li><Link to="/mentions-legales" className="hover:text-[var(--sl-primary)]">Mentions légales</Link></li>
            <li><Link to="/conditions" className="hover:text-[var(--sl-primary)]">Conditions d&apos;utilisation</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-heading font-semibold mb-3">Service client</h4>
          <ul className="text-sm space-y-2.5 text-gray-600">
            {SUPPORT_NUMBERS.map((s) => {
              // Admin can override the number per corridor via site-content (whatsapp_support_<country>)
              const overrideKey = `whatsapp_support_${s.country.toLowerCase()}`;
              const override = (sc[overrideKey] || "").trim();
              const digits = (override || s.phone).replace(/\D/g, "");
              const displayPhone = override ? `+${digits}` : s.phone;
              const waLink = `https://wa.me/${digits}`;
              return (
                <li key={s.country}>
                  <a
                    href={waLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 hover:text-[var(--sl-primary)] group"
                    data-testid={`support-${s.country.toLowerCase()}`}
                  >
                    <span className="text-base">{s.flag}</span>
                    <span className="font-mono text-xs group-hover:underline">{displayPhone}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <div className="border-t border-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center gap-3 text-xs text-gray-500">
          <span>© {new Date().getFullYear()} Swiftlink-Africa. Tous droits réservés.</span>
        </div>
      </div>
    </footer>
  );
}
